/**
 * Branded document renderer — wraps a BusinessDoc's editable markdown body
 * in a self-contained, on-brand ArgoIQ HTML page for preview and PDF
 * export. Follows the Argo Design Standard: dark cover scheme, magenta/steel
 * accent, Manrope + Geist Mono, inlined wordmark so the file is portable.
 *
 * Features: Full-bleed dark hero, generous whitespace, glass-style callouts,
 * borderless premium tables, dark-theme throughout, print-safe colors & breaks.
 *
 * The markdown parser handles: h1-h4, bold/italic/code, bullet+numbered lists,
 * tables (with separator detection), blockquotes (glass cards), horizontal rules,
 * and paragraphs. It is not a general CommonMark implementation — optimized for
 * our own proposal & contract templates.
 */

import type { BusinessDoc } from '../db/types';

/** Inlined white wordmark from the brand kit (logo-wordmark-white.svg).
 *  Exported so sibling renderers (e.g. crm/proposalDoc) reuse the same
 *  brand asset instead of duplicating the SVG. */
export const WORDMARK_WHITE =
  '<svg viewBox="0 0 130 18" width="130" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M46.14 1.227h.036c10.035.016 10.119.016 10.636.132.287.06.724.192.966.286.243.088.59.263.772.379.182.115.452.346.601.511.15.165.364.496.48.738.116.242.248.627.298.852.055.259.1.82.122 1.513.022.87.01 1.221-.062 1.667-.05.308-.148.715-.215.902a4.277 4.277 0 01-.287.594c-.088.138-.28.38-.435.545a3.57 3.57 0 01-.64.5 4.088 4.088 0 01-.568.281c-.11.044-.204.088-.204.105.002.017.233.137.508.274.347.17.607.342.822.55.17.166.397.44.502.605.104.165.247.457.313.644.06.192.155.561.2.825.06.358.082.903.082 2.064 0 1.286-.016 1.622-.083 1.776a.778.778 0 01-.264.303c-.16.099-.276.11-1.325.11-.903 0-1.174-.017-1.306-.083a.73.73 0 01-.26-.259c-.083-.16-.1-.419-.154-3.811l-.171-.359c-.11-.22-.27-.44-.414-.566a2.383 2.383 0 00-.48-.313 3.98 3.98 0 00-.463-.176c-.185-.05-2.227-.072-16.316-.099l-.005 2.514c0 1.804-.022 2.574-.066 2.712a.673.673 0 01-.26.313c-.187.126-.232.132-1.334.126-.893 0-1.192-.016-1.34-.082a.506.506 0 01-.276-.275c-.072-.171-.083-1.151-.083-7.729 0-6.579.011-7.558.083-7.728a.518.518 0 01.265-.276h-.006c.16-.071 1.489-.076 10.33-.065zm57.01-.012c7.869-.005 8.908.006 9.349.083.276.05.657.143.855.21.199.065.535.214.745.335.226.121.546.369.772.6.221.22.485.56.607.775.121.209.27.544.336.742.072.198.166.594.215.88.078.457.083 1.057.067 4.676-.017 3.762-.028 4.192-.117 4.566a7.43 7.43 0 01-.242.77c-.077.198-.226.49-.325.65a5.032 5.032 0 01-.552.66c-.22.22-.535.467-.761.588-.21.121-.546.27-.745.335-.198.066-.579.16-.855.21-.441.076-1.491.087-9.294.087-7.567 0-8.864-.01-9.266-.082a8.099 8.099 0 01-.772-.17 6.028 6.028 0 01-.745-.314 4.083 4.083 0 01-.788-.56 4.46 4.46 0 01-.585-.688 4.697 4.697 0 01-.348-.633 5.512 5.512 0 01-.231-.716c-.072-.259-.144-.786-.177-1.292-.039-.528-.05-2.14-.033-4.236.016-2.833.038-3.466.11-3.895.055-.275.15-.66.22-.847.067-.192.222-.522.338-.731.132-.231.38-.55.633-.804.298-.291.547-.478.86-.637.244-.127.58-.264.746-.314.165-.05.49-.12.717-.165.342-.06 1.997-.077 9.266-.083zm-26.652 0c9.415-.005 10.006 0 10.193.089.11.055.237.165.276.247.055.116.077.412.077 1.116 0 .71-.022 1.001-.077 1.112a.825.825 0 01-.171.237c-.091.071-1.285.087-9.675.11-8.867.027-9.596.038-9.878.126-.165.05-.403.154-.524.226a1.928 1.928 0 00-.381.313c-.088.105-.238.348-.502.903V12.9l.172.357c.094.198.242.445.336.55.094.104.286.259.435.336.15.082.409.181.575.22.249.066 1.668.077 8.025.077 7.521 0 7.732-.006 8.107-.11.21-.061.48-.177.596-.26.127-.093.265-.274.358-.466.15-.319.15-.33.177-2.871v-.006H80.95c-3.136 0-3.694-.012-3.86-.083a.508.508 0 01-.276-.276c-.061-.143-.084-.412-.084-1.072 0-.66.023-.929.084-1.072a.508.508 0 01.276-.276c.166-.07.885-.082 5.283-.082 4.983 0 5.091 0 5.268.11.093.06.215.182.26.275.071.138.082.562.082 2.734 0 1.435-.028 2.782-.055 3.042a5.27 5.27 0 01-.177.825c-.06.198-.17.473-.242.616-.072.149-.326.457-.558.693-.264.259-.562.495-.76.6-.183.094-.491.226-.69.286a8.79 8.79 0 01-.856.214c-.452.082-1.314.094-9.183.11-6.461.017-8.806 0-9.155-.044-.26-.038-.63-.11-.828-.165a4.845 4.845 0 01-.772-.29 4.267 4.267 0 01-.723-.464 5.624 5.624 0 01-.545-.539 4.359 4.359 0 01-.442-.714 6.373 6.373 0 01-.326-.826 7.022 7.022 0 01-.171-.99c-.028-.341-.055-2.096-.055-4.043s.027-3.701.055-4.043c.033-.33.11-.781.17-.99.062-.21.211-.583.327-.825.116-.242.32-.561.441-.715.127-.149.376-.39.546-.54.171-.142.508-.357.75-.473.243-.115.568-.242.717-.28.155-.039.453-.1.662-.138.3-.055 2.732-.071 10.386-.077zm34.562 2.937c-11.836-.011-15.413 0-15.582.022a3.698 3.698 0 00-.635.16 1.79 1.79 0 00-.6.38c-.15.148-.332.39-.398.538a2.657 2.657 0 00-.183.633c-.038.22-.06 1.552-.06 3.41 0 1.86.022 3.191.06 3.411.028.198.111.484.183.632.066.154.237.385.375.523.154.154.374.298.573.375.182.071.524.153.767.18.27.033 3.32.05 7.97.04l7.53-.017c.722-.264.893-.369 1.091-.567.144-.143.326-.38.398-.534.077-.148.159-.446.192-.66.028-.221.056-1.679.056-3.383 0-1.666-.028-3.162-.056-3.366a2.676 2.676 0 00-.215-.688 2.406 2.406 0 00-.402-.55c-.165-.165-.359-.28-.651-.385l-.413-.154zm-72.229-.028V8.8c15.852-.027 16.431-.032 16.74-.126.182-.055.43-.165.552-.248.149-.099.276-.259.386-.478.16-.325.165-.347.181-1.32.011-.578-.005-1.106-.038-1.265a1.946 1.946 0 00-.237-.545c-.138-.204-.26-.303-.513-.424a2.603 2.603 0 00-.8-.214c-.303-.033-3.293-.056-8.372-.056H38.83zM13.034 1.006c.328 0 .53.36.359.64l-9.155 14.93c-.268.352-.624.558-1.21.6H.75a.42.42 0 01-.358-.64L9 2.496c.586-.933 1.171-1.432 2.272-1.49h1.761zm12.795.007c1.744.008 2.662.024 2.81.048.22.033.59.11.816.17a5.6 5.6 0 01.855.336c.264.138.585.357.795.56.192.182.451.48.573.661.121.182.292.49.369.688.083.198.188.517.237.715.05.199.117.627.15.962.033.369.055 2.702.05 5.996 0 4.027-.017 5.441-.067 5.584a.674.674 0 01-.26.314c-.187.126-.232.131-1.334.126-.893 0-1.192-.016-1.34-.082a.58.58 0 01-.276-.248c-.07-.136-.083-.515-.083-4.497H12.08l-2.595 4.231c-.265.347-.613.553-1.185.598H6a.42.42 0 01-.358-.64l8.608-14.04c.586-.933 1.171-1.432 2.272-1.49h9.306v.008zm0 2.932h-7.546c-.812.01-1.223.289-1.533.805v-.02l-2.738 4.465h15.112c0-2.279-.028-3.152-.06-3.409a2.226 2.226 0 00-.221-.781 2.342 2.342 0 00-.392-.54 1.85 1.85 0 00-.54-.334 3.857 3.857 0 00-.69-.16 33.56 33.56 0 00-1.392-.027v.001zM118.431 7.875V6.522h1.392V2.509h-1.392V1.156h4.316v1.353h-1.391v4.013h1.391v1.353h-4.316zm8.618.662l-1.343-2.877h1.031l1.344 2.877h-1.032zm-.785-.51c-.543 0-1.006-.136-1.391-.408-.379-.27-.669-.665-.871-1.183-.196-.523-.293-1.16-.293-1.911 0-.751.097-1.388.293-1.912.202-.524.492-.921.871-1.192.385-.278.848-.417 1.391-.417s1.003.139 1.382.417c.385.27.675.668.87 1.192.202.524.303 1.16.303 1.912 0 .75-.101 1.388-.303 1.911-.195.518-.485.912-.87 1.183-.379.272-.839.407-1.382.407zm0-1.326a.744.744 0 00.549-.217c.151-.152.262-.385.331-.7.076-.322.114-.742.114-1.26 0-.517-.038-.936-.114-1.258-.069-.322-.18-.558-.331-.71a.728.728 0 00-.549-.227.747.747 0 00-.549.227c-.151.152-.265.388-.341.71-.069.322-.104.741-.104 1.259 0 .517.035.937.104 1.258.076.316.19.55.341.7a.763.763 0 00.549.218z" fill="#fff"/></svg>';

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Inline formatting: **bold**, _italic_, `code`. Operates on escaped text. */
function inline(s: string): string {
  return esc(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])_([^_]+)_/g, (_m, pre, p1) => pre + '<em>' + p1 + '</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}

/** Render a markdown table block (rows already split, includes separator).
 *  Detects signature tables (2 cols × 2-3 rows) and renders them specially.
 */
function renderTable(rows: string[]): string {
  const cells = (line: string) =>
    line
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((c) => c.trim());
  const header = cells(rows[0]);
  const bodyRows = rows.slice(2).map(cells);

  // Standard table: header row + body rows. Both the pricing and signature
  // blocks render through this path — the signature block (parties as the
  // header, then signatory / signature-line / date rows) reads cleanly as a
  // premium table, and the pricing total row is highlighted below.
  const thead =
    '<thead><tr>' +
    header.map((c) => `<th>${inline(c)}</th>`).join('') +
    '</tr></thead>';
  const tbody =
    '<tbody>' +
    bodyRows
      .map((r) => {
        // Highlight only genuine total rows. Strip markdown emphasis first so
        // `**Total 12-month value**` is matched; do NOT blanket-highlight the
        // last row (that wrongly tinted the timeline + signature date rows).
        const isTotalRow = r.some((cell) =>
          /^(total|grand total)/i.test(cell.replace(/\*/g, '').trim()),
        );
        const rowClass = isTotalRow ? ' class="total-row"' : '';
        return (
          '<tr' +
          rowClass +
          '>' +
          r.map((c) => `<td>${inline(c)}</td>`).join('') +
          '</tr>'
        );
      })
      .join('') +
    '</tbody>';
  return `<table>${thead}${tbody}</table>`;
}

/** Minimal markdown → HTML for our own template output. */
function mdToHtml(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      out.push('<hr/>');
      i++;
      continue;
    }

    // Headings — cover owns h1, so body sections start at h2. `##` (our
    // section level) → h2 branded section style; `###` → h3 subheading.
    const h = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (h) {
      const level = Math.max(2, h[1].length);
      out.push(`<h${level}>${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // Blockquote → accent callout (used for the guarantee / accept CTA).
    if (trimmed.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quote.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${inline(quote.join(' '))}</blockquote>`);
      continue;
    }

    // Table: a line starting with | followed by a |---| separator
    if (trimmed.startsWith('|') && lines[i + 1] && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const block: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        block.push(lines[i].trim());
        i++;
      }
      out.push(renderTable(block));
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // Bullet list
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    // Paragraph (collect until blank line)
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|[-*]\s|\d+\.\s|\||>|---+$)/.test(lines[i].trim())) {
      para.push(lines[i].trim());
      i++;
    }
    out.push(`<p>${inline(para.join(' '))}</p>`);
  }
  return out.join('\n');
}

function fmtUsd(n?: number): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Produce a complete, standalone HTML document for a proposal/contract,
 * ready to open in a new tab and print to PDF (Cmd/Ctrl-P).
 */
export function renderBrandedDocHtml(doc: BusinessDoc): string {
  const isProposal = doc.kind === 'proposal';
  const kindLabel = isProposal ? 'Proposal' : 'Service Agreement';
  const accentColor = isProposal ? '#FF00FF' : '#2D6BFF';
  const accentColorDeep = isProposal ? '#BB4198' : '#2454CC';
  const body = mdToHtml(doc.body);
  const owner = doc.ownerName?.trim() || 'Argo IQ';

  return `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${esc(doc.title)} · Argo IQ</title>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;700;800&family=Geist+Mono:wght@400;500&display=swap" rel="stylesheet"/>
<style>
  :root{
    --accent:${accentColor}; --accent-deep:${accentColorDeep};
    --orange:#FDAD01; --orange-text:#E23939; --blue:#0082F3; --green:#22C55E;
    --ink:#121212; --ink-soft:#222222; --body:#444444; --muted:#666666; --faint:#AAAAAA;
    --line:#EEEEEE; --paper:#FFFFFF; --paper-2:#F5F5F5;
    --dark-bg:#0a0812; --text-light:#f5f5fa;
    --mono:'Geist Mono',ui-monospace,Menlo,monospace;
    --r-sm:8px; --r-md:16px; --r-lg:32px;
  }
  *{box-sizing:border-box;}
  html,body{margin:0;padding:0;}
  body{
    font-family:'Manrope',system-ui,sans-serif;background:#e9e9ef;color:var(--body);
    -webkit-print-color-adjust:exact;print-color-adjust:exact;
  }
  .page{
    max-width:1100px;margin:24px auto;background:var(--paper);color:var(--body);
    box-shadow:0 20px 60px rgba(0,0,0,0.18);overflow:hidden;border-radius:var(--r-md);
  }
  /* ---- Cover (full-bleed dark hero) ---- */
  .cover{
    position:relative;background:var(--dark-bg);color:var(--text-light);
    padding:clamp(64px,7vw,104px) clamp(40px,6vw,72px) clamp(56px,6vw,80px);
    overflow:hidden;
  }
  .cover::before{
    content:'';position:absolute;inset:0;
    background:radial-gradient(ellipse 800px 600px at 70% -20%, rgba(255,0,255,0.25) 0%, transparent 50%);
    pointer-events:none;
  }
  ${isProposal ? '.cover::after{content:\'\';position:absolute;inset:0;background:radial-gradient(ellipse 600px 500px at 20% 110%, rgba(187,65,152,0.12) 0%, transparent 60%);pointer-events:none;}' : ''}
  .cover .motif{position:absolute;right:-160px;top:20px;width:680px;opacity:${isProposal ? '0.22' : '0.15'};pointer-events:none;stroke:rgba(255,255,255,0.1);}
  .cover .wm{height:20px;width:auto;display:block;margin-bottom:48px;position:relative;z-index:1;}
  .cover .eyebrow{
    font-family:var(--mono);text-transform:uppercase;letter-spacing:.14em;
    font-size:13px;color:var(--accent);margin:0 0 14px;position:relative;z-index:1;
  }
  .cover h1{
    font-size:clamp(2.5rem,5.5vw,4rem);line-height:1.05;font-weight:800;margin:0 0 24px;
    letter-spacing:-0.02em;max-width:16ch;position:relative;z-index:1;
  }
  .cover .sub{font-size:1.1875rem;line-height:1.55;color:rgba(255,255,255,0.72);margin:0;max-width:58ch;position:relative;z-index:1;}
  .cover .meta{
    display:grid;gap:28px;margin-top:48px;position:relative;z-index:1;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));
  }
  .cover .meta div{display:flex;flex-direction:column;gap:6px;}
  .cover .meta .k{font-family:var(--mono);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,0.4);}
  .cover .meta .v{font-size:15px;font-weight:600;color:var(--text-light);}
  .cover .value{
    margin-top:32px;display:inline-flex;align-items:baseline;gap:14px;position:relative;z-index:1;
    border:1px solid rgba(255,255,255,0.14);border-radius:var(--r-md);padding:18px 26px;
    /* WHY: Safari requires the -webkit- prefix for backdrop-filter; without it
       the glass badge silently fails on WebKit (TIQ-005). Keep both prefixes. */
    background:rgba(255,255,255,0.05);
    -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
    -webkit-print-color-adjust:exact;print-color-adjust:exact;
  }
  .cover .value .k{font-family:var(--mono);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,0.4);}
  .cover .value .n{font-size:30px;font-weight:800;color:var(--accent);}
  /* ---- Body (LIGHT theme, matching proposals.argoiq.com) ---- */
  .doc{padding:clamp(40px,5vw,64px) clamp(40px,6vw,72px);}
  .doc h2{
    position:relative;font-size:clamp(1.75rem,3vw,2.5rem);font-weight:800;line-height:1.12;
    letter-spacing:-0.01em;color:var(--ink);margin:clamp(40px,5vw,64px) 0 20px;
    padding-top:36px;border-top:1px solid var(--line);
  }
  .doc h2::before{
    content:'';display:block;height:3px;width:44px;
    background:linear-gradient(90deg, var(--accent), var(--accent-deep));
    margin-bottom:18px;border-radius:2px;
  }
  .doc h2:first-child{margin-top:0;padding-top:0;border-top:none;}
  .doc h3{font-size:1.75rem;font-weight:700;line-height:1.2;margin:28px 0 12px;color:var(--ink-soft);}
  .doc h4{font-size:1.25rem;font-weight:600;line-height:1.3;margin:18px 0 8px;color:var(--ink-soft);}
  .doc p{font-size:1rem;line-height:1.6;color:var(--body);margin:0 0 16px;max-width:70ch;}
  .doc strong{color:var(--ink);font-weight:700;}
  .doc em{font-style:italic;color:var(--muted);}
  .doc ul,.doc ol{margin:0 0 18px;padding-left:24px;max-width:70ch;}
  .doc li{font-size:1rem;line-height:1.6;color:var(--body);margin:0 0 10px;}
  .doc li strong{color:var(--ink);}
  .doc code{font-family:var(--mono);font-size:0.875rem;background:var(--paper-2);padding:2px 8px;border-radius:6px;color:var(--accent-deep);border:1px solid var(--line);}
  .doc hr{border:none;border-top:1px solid var(--line);margin:36px 0;}
  /* ---- Tables (premium, borderless) ---- */
  .doc table{width:100%;border-collapse:collapse;margin:20px 0 28px;font-size:0.9375rem;}
  .doc thead{display:table-header-group;}
  .doc th{
    text-align:left;font-family:var(--mono);font-size:0.75rem;letter-spacing:.08em;text-transform:uppercase;
    color:var(--muted);padding:12px 16px;border-bottom:2px solid var(--ink);font-weight:500;
  }
  .doc td{padding:14px 16px;border-bottom:1px solid var(--line);color:var(--body);}
  .doc tr:last-child td{border-bottom:none;}
  .doc tr.total-row td{
    background:rgba(255,0,255,0.06);color:var(--accent-deep);font-weight:700;border-bottom:none;
  }
  .doc tr.total-row td strong{color:var(--accent-deep);}
  /* ---- Callout (blockquote) — guarantee / accept CTA card ---- */
  .doc blockquote{
    margin:28px 0 32px;padding:28px 32px;border-radius:var(--r-md);
    border:1px solid var(--line);border-left:4px solid var(--accent);
    background:var(--paper-2);
    font-size:1.0625rem;line-height:1.65;color:var(--ink-soft);font-weight:500;
  }
  .doc blockquote strong{color:var(--accent-deep);font-weight:700;}
  .doc blockquote em{color:var(--accent-deep);font-style:normal;font-weight:600;}
  .doc blockquote code{background:#fff;color:var(--accent-deep);}
  /* ---- Footer (dark) ---- */
  .foot{
    background:var(--ink);color:rgba(255,255,255,0.6);padding:28px clamp(40px,6vw,72px);
    display:flex;align-items:center;justify-content:space-between;gap:20px;
    font-family:var(--mono);font-size:11px;letter-spacing:.08em;text-transform:uppercase;
  }
  .foot .wm{height:16px;width:auto;opacity:0.85;}
  /* ---- Print CSS: light body, dark cover/footer, clean breaks ---- */
  @media print{
    html,body{background:#fff;margin:0;padding:0;}
    .page{
      max-width:none;margin:0;box-shadow:none;border-radius:0;background:#fff;
    }
    @page{margin:0;size:A4;}
    .cover{page-break-after:always;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    /* WHY (TIQ-005): Safari's print/PDF engine does not composite
       backdrop-filter, so the translucent "Engagement value" badge rendered
       blank on export (worked in Chrome). Drop the blur in print and paint an
       explicit solid fill + colors so the badge holds across Safari & Chrome. */
    .cover .value{
      -webkit-backdrop-filter:none;backdrop-filter:none;
      background:rgba(255,255,255,0.10);border-color:rgba(255,255,255,0.22);
      -webkit-print-color-adjust:exact;print-color-adjust:exact;
    }
    .cover .value .k{color:rgba(255,255,255,0.55);}
    .cover .value .n{color:var(--accent);-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .doc{page-break-inside:auto;}
    .doc h2{page-break-after:avoid;page-break-before:auto;}
    .doc h3{page-break-after:avoid;}
    .doc table,.doc ul,.doc ol,.doc blockquote{page-break-inside:avoid;}
    .foot{page-break-before:avoid;}
  }
</style>
</head>
<body>
  <div class="page">
    <header class="cover">
      <svg class="motif" viewBox="0 0 1020 367" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M509.6 0L1019.2 183.12L509.6 366.24L0 183.12L509.6 0Z" fill="rgba(255,0,255,0.12)"/><path d="M1017.54 183.12L509.6 365.645L1.65625 183.12L509.6 0.594727L1017.54 183.12Z" stroke="rgba(255,255,255,0.12)" stroke-width="1.2"/></svg>
      <div class="wm">${WORDMARK_WHITE}</div>
      <p class="eyebrow">${esc(kindLabel)}</p>
      <h1>${esc(doc.entityName)}</h1>
      <p class="sub">${isProposal ? 'An engagement to stand up your AI operating layer — measured against your AQI score.' : 'Master terms for the Argo IQ managed AI-operations engagement.'}</p>
      <div class="meta">
        <div><span class="k">Prepared by</span><span class="v">${esc(owner)} · Argo IQ</span></div>
        <div><span class="k">Date</span><span class="v">${fmtDate(doc.createdAt)}</span></div>
        <div><span class="k">Status</span><span class="v" style="text-transform:capitalize">${esc(doc.status)}</span></div>
      </div>
      ${
        doc.value != null
          ? `<div class="value"><span class="k">Engagement value</span><span class="n">${fmtUsd(doc.value)}</span></div>`
          : ''
      }
    </header>
    <main class="doc">${body}</main>
    <footer class="foot">
      <div class="wm">${WORDMARK_WHITE}</div>
      <span>Argo IQ · ${esc(doc.title)}</span>
    </footer>
  </div>
</body></html>`;
}
